import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { ApiError } from '@/components/Functional/lib/request'
import { extractApiErrorMessage } from '@/components/Functional/utils/apiErrorMessage'
import { resolveRedirectUri } from '@/components/Functional/utils/resolveRedirectUri'
import { shouldRetryApiError } from '@/components/Functional/utils/shouldRetryApiError'
import { Header } from '@/components/Layout/Header'
import { DialogLoginRequired } from '@/components/Models/dialog/components/DialogLoginRequired'
import { TicketSelectList } from '@/components/Pages/ec/components/TicketSelectList'
import { Button } from '@/components/UI/Button'
import { Snackbar } from '@/components/UI/Snackbar'
import type { ProductListItem } from '@/schemas/v2/ec'
import { useTokenStore } from '@/store/useTokenStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/** 現在のオリジンを返す（SSR / 静的エクスポート時は NEXT_PUBLIC_SITEURL にフォールバック）。 */
function currentOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : (process.env.NEXT_PUBLIC_SITEURL ?? '')
}

/** origin を付与して絶対URLに変換する（Stripe の success/cancel URL は絶対URL必須）。 */
function toAbsoluteUrl(path: string): string {
  return `${currentOrigin()}${path}`
}

/** チケット選択画面（EC / 体験チケット購入）。 */
export const TicketSelectPage = () => {
  const api = useApi()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { campaignSlug, gen, loginUrl } = useCampaignSlug()

  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null

  const [loginOpen, setLoginOpen] = useState(false)
  const [submittingProductId, setSubmittingProductId] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // 戻る先: `?redirect_uri=` があればその画面へ（外部オリジンも可）、無ければキャンペーンのホームへ。
  // router.back() を使わないのは、Stripe Checkout の「戻る」が history.back() ではなく
  // 通常遷移（アンカーの push）で cancelUrl に着地するため、直前の履歴エントリが
  // Checkout になっているから。back() すると Checkout へ戻り、そこで再度「戻る」を押すと
  // チケット選択 ⇄ Checkout の往復ループになる。
  const redirect = router.isReady ? resolveRedirectUri(router.query.redirect_uri, currentOrigin()) : null
  const backUrl = redirect?.href ?? gen('/')

  // 履歴を増やさず push ではなく replace で戻る。
  // push だとブラウザバックでチケット選択（＝その先の Checkout）へ再突入できてしまう。
  // 外部オリジンは next/router を通さない（外部URLだと内部で `window.location.href = url` に
  // フォールバックし、replace ではなく push 相当になってしまうため）。
  const handleBack = () => {
    if (redirect?.isExternal === true) {
      window.location.assign(backUrl)
      return
    }
    void router.push(backUrl)
  }

  // 商品一覧は未認証時「前提購入なし商品のみ」を返す契約のため、認証済みは auth で取得する。
  // queryKey を auth/guest で分離し、ログイン状態間でのキャッシュ不整合を避ける。
  const {
    data,
    isLoading,
    isError: isProductsError,
    error: productsError,
    isFetching: isProductsFetching,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ['products', 'TICKET', campaignSlug, isAuthenticated ? 'auth' : 'guest'],
    queryFn: () => (isAuthenticated ? api.auth : api.guest).fetchProducts({ productType: 'TICKET' }),
    retry: shouldRetryApiError,
  })

  const handleSelect = async (product: ProductListItem) => {
    // 一覧の purchasable で購入不可（購入済み・売り切れ等）は非活性済み。
    // 在庫の競合等の最終判定は引き続きチェックアウトでサーバが行う
    if (submittingProductId !== null) return

    // 未ログインはログイン誘導ダイアログを表示する
    if (!isAuthenticated) {
      setLoginOpen(true)
      return
    }

    setSubmittingProductId(product.id)
    setErrorMessage(null)
    try {
      const res = await api.auth.createCheckoutSession({
        productId: product.id,
        quantity: 1,
        // {CHECKOUT_SESSION_ID} は Stripe がリダイレクト時に実際のセッションIDへ置換するプレースホルダ
        // （管理画面のフロント用プリセットと同じ形式）。エンコードせずそのまま渡す。
        successUrl: `${toAbsoluteUrl(gen('/payment/complete'))}?session_id={CHECKOUT_SESSION_ID}`,
        // キャンセル時はチケット選択へ戻す。redirect_uri は引き継がないと Checkout 往復後に
        // 戻る先を見失うため、付与された場合のみ再度クエリに載せる。
        cancelUrl: toAbsoluteUrl(
          redirect !== null
            ? `${gen('/products/tickets')}?redirect_uri=${encodeURIComponent(redirect.href)}`
            : gen('/products/tickets'),
        ),
      })
      // 決済開始時: 購入により購入連動ロックが解放されるため sheets/spots キャッシュを削除し、
      // 決済完了後の遷移で最新のロック状態を取得させる
      queryClient.removeQueries({ queryKey: ['sheets'] })
      queryClient.removeQueries({ queryKey: ['spots'] })
      // Stripe ホストの Checkout ページへリダイレクト。
      // assign なので履歴エントリが残り、Checkout からブラウザバックでこの画面に戻れる。
      // ※ next/router の push/replace は外部URLだと内部で `window.location.href = url` へ
      //   フォールバックするため、location を直接使う。
      window.location.assign(res.checkoutUrl)
    } catch (e) {
      // 401 は認証切れ → ログイン誘導、それ以外はステータス別メッセージ
      if (e instanceof ApiError && e.status === 401) {
        setLoginOpen(true)
      } else {
        setErrorMessage(resolveCheckoutErrorMessage(e))
      }
      setSubmittingProductId(null)
    }
  }

  /**
   * チェックアウト作成エラーのメッセージを決定する。
   * バックエンドが利用者向けに公開したメッセージ（購入条件未達・販売期間外の具体的な理由）を最優先し、
   * 公開されていない場合のみ status ごとの i18n 文言にフォールバックする。
   */
  function resolveCheckoutErrorMessage(e: unknown): string {
    const serverMessage = extractApiErrorMessage(e)
    if (serverMessage !== null) return serverMessage

    if (e instanceof ApiError) {
      switch (e.status) {
        case 400:
          return t('ec.checkout.error.unavailable', '現在ご購入いただけません（販売期間外の可能性があります）。')
        case 409:
          return t('ec.checkout.error.sold_out', '売り切れ、または購入条件を満たしていません。')
        case 503:
          return t('ec.checkout.error.service', '決済サービスに接続できません。時間をおいて再度お試しください。')
      }
    }
    return t('ec.ticket_select.checkout_error', '決済の開始に失敗しました。時間をおいて再度お試しください。')
  }

  return (
    // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
    // min-h-dvh だとラッパーごと伸びて overflow-y-auto が効かず、
    // ドキュメント全体がスクロールしてスクロールバーがヘッダー領域まで伸びる。
    <div className="relative flex h-dvh flex-col bg-white">
      <Header title={t('ec.ticket_select.title', '販売')} showBack onBack={handleBack} showNotification={false} />
      <main className="min-h-0 flex-1 overflow-y-auto py-4 pb-[88px]">
        {isProductsError && !data ? (
          // 取得失敗を空配列として扱うと「販売中のチケットはありません」と誤表示されるため分岐する。
          // `!data` を条件に含めるのは、表示済みの一覧がバックグラウンド再取得の失敗で
          // エラー表示に置き換わらないようにするため。
          <div className="flex flex-col items-center gap-4 px-4 py-10 text-center">
            <p className="text-sm text-gray-500">
              {extractApiErrorMessage(productsError) ??
                t('ec.ticket_select.load_error', 'チケットの取得に失敗しました。時間をおいて再度お試しください。')}
            </p>
            <Button
              label={t('common.reload', '再読み込み')}
              variant="secondary"
              size="md"
              // 再取得中は連打を防ぐ（エラー表示のままなので押下できたように見えてしまう）
              disabled={isProductsFetching}
              onClick={() => refetchProducts()}
            />
          </div>
        ) : (
          <TicketSelectList
            products={data?.products ?? []}
            loading={isLoading}
            submittingProductId={submittingProductId}
            onSelect={handleSelect}
          />
        )}
      </main>

      {errorMessage && (
        <div className="fixed inset-x-0 bottom-[80px] z-50 flex justify-center px-4">
          <Snackbar message={errorMessage} onClose={() => setErrorMessage(null)} />
        </div>
      )}

      <DialogLoginRequired opened={loginOpen} onClose={() => setLoginOpen(false)} loginUrl={loginUrl} />
    </div>
  )
}
